import type { FilterSidebarForm, BrandItemPair } from "./filter-sidebar-form";
import { Roles } from "../../constants/roles";

// All params to clear when "Clear all" is clicked
const ALL_FILTER_PARAMS = ["parent_category_id", "category_id", "brand_id", "price_range", "search"] as const;

export class FilterSidebarHandler {
    /**
     * Build URL for brand/price_range checkbox changes.
     * Category and search params in the current URL are preserved.
     */
    public buildUrl(form: FilterSidebarForm): string {
        const current = new URLSearchParams(globalThis.location.search);
        current.delete("brand_id");
        current.delete("price_range");

        const brandIds: string[] = [];
        const priceRanges: string[] = [];

        form.getCheckboxes().forEach((cb) => {
            if (!cb.checked) return;
            const param = cb.dataset.filterParam;
            const value = cb.value;
            if (!value) return;
            if (param === "brand_id") brandIds.push(value);
            else if (param === "price_range") priceRanges.push(value);
        });

        if (brandIds.length) current.set("brand_id", brandIds.join(","));
        if (priceRanges.length) current.set("price_range", priceRanges.join(","));
        current.delete("page");

        const qs = current.toString();
        return qs ? `${globalThis.location.pathname}?${qs}` : globalThis.location.pathname;
    }

    /** Build URL for category picker selection. Replaces only category params. */
    public buildUrlForCategorySelect(filterParam: string, catId: string): string {
        const current = new URLSearchParams(globalThis.location.search);
        current.delete("parent_category_id");
        current.delete("category_id");
        current.delete("page");
        if (catId && filterParam !== "none") {
            current.set(filterParam, catId);
        }
        const qs = current.toString();
        return qs ? `${globalThis.location.pathname}?${qs}` : globalThis.location.pathname;
    }

    /** Build URL for keyword search. Replaces only the search param. */
    public buildUrlForSearch(searchValue: string): string {
        const current = new URLSearchParams(globalThis.location.search);
        current.delete("search");
        current.delete("page");
        if (searchValue.trim()) {
            current.set("search", searchValue.trim());
        }
        const qs = current.toString();
        return qs ? `${globalThis.location.pathname}?${qs}` : globalThis.location.pathname;
    }

    public buildClearUrl(): string {
        const current = new URLSearchParams(globalThis.location.search);
        ALL_FILTER_PARAMS.forEach((p) => current.delete(p));
        current.delete("page");
        const qs = current.toString();
        return qs ? `${globalThis.location.pathname}?${qs}` : globalThis.location.pathname;
    }

    /** Restore brand/price_range checkbox state from URL. */
    public restoreCheckedState(form: FilterSidebarForm): void {
        const params = new URLSearchParams(globalThis.location.search);
        const activeBrandIds = new Set((params.get("brand_id") ?? "").split(",").filter(Boolean));
        const activePriceRanges = new Set((params.get("price_range") ?? "").split(",").filter(Boolean));

        form.getCheckboxes().forEach((cb) => {
            const param = cb.dataset.filterParam;
            const value = cb.value;
            if (param === "brand_id") cb.checked = activeBrandIds.has(value);
            else if (param === "price_range") cb.checked = activePriceRanges.has(value);
        });
    }

    /** Restore picker label and active state from URL. */
    public restoreCategoryPickerState(form: FilterSidebarForm, defaultLabel: string): void {
        const params = new URLSearchParams(globalThis.location.search);
        const parentCatId = params.get("parent_category_id");
        const childCatId = params.get("category_id");
        const activeId = childCatId ?? parentCatId;

        const label = form.getCategoryPickerLabel();
        const items = form.getCategoryPickerItems();

        items.forEach((i) => { i.classList.remove("is-active"); i.removeAttribute("aria-selected"); });

        if (!activeId) {
            // No category selected — activate the ALL button
            const allItem = items.find((i) => i.dataset.filterParam === "none");
            if (allItem) {
                allItem.classList.add("is-active");
                allItem.setAttribute("aria-selected", "true");
            }
            return;
        }

        const match = items.find((i) => {
            if (childCatId) return i.dataset.filterParam === "category_id" && i.dataset.catId === childCatId;
            return i.dataset.filterParam === "parent_category_id" && i.dataset.catId === parentCatId;
        });

        if (!match) return;

        match.classList.add("is-active");
        match.setAttribute("aria-selected", "true");
        if (label) label.textContent = match.dataset.catLabel || defaultLabel;

        // Auto-expand parent when a child is selected
        if (childCatId) {
            const group = form.getPickerGroupOf(match);
            if (group) {
                const childrenEl = group.querySelector(`[data-role="${Roles.FILTER_SIDEBAR_CAT_CHILDREN}"]`);
                const toggleBtn = group.querySelector(`[data-role="${Roles.FILTER_SIDEBAR_CAT_TOGGLE}"]`);
                if (childrenEl) childrenEl.classList.add("is-open");
                if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "true");
            }
        }
    }

    /**
     * Restore keyword input value from URL — always sets it (including
     * clearing to "" when the URL has no `search` param), rather than only
     * setting it when present. Some browsers restore an input's previous
     * value on navigation despite `autocomplete="off"` and no server-rendered
     * `value` attribute (e.g. after selecting a search-suggest item that
     * navigates straight to a product/brand page with no `search` param);
     * without an explicit clear here, that residual text — and its clear
     * button, kept in sync via registerKeywordSearch's syncClear() — stays
     * visible even though the URL no longer reflects any active search.
     */
    public restoreKeywordSearch(form: FilterSidebarForm): void {
        const params = new URLSearchParams(globalThis.location.search);
        const search = params.get("search") ?? "";
        const input = form.getKeywordSearchInput();
        if (input) input.value = search;
    }

    public filterPickerCategories(query: string, groups: HTMLElement[]): void {
        const q = query.toLowerCase().trim();
        groups.forEach((group) => {
            const parentItem = group.querySelector(`[data-role="${Roles.FILTER_SIDEBAR_CAT_PICKER_ITEM}"][data-filter-param="parent_category_id"]`) as HTMLElement | null;
            const childItems = Array.from(group.querySelectorAll(`[data-role="${Roles.FILTER_SIDEBAR_CAT_PICKER_ITEM}"][data-filter-param="category_id"]`)) as HTMLElement[];

            if (!parentItem) return;
            const parentName = (parentItem.querySelector("span")?.textContent ?? "").toLowerCase();
            const parentMatches = q === "" || parentName.includes(q);

            let hasMatchingChild = false;
            childItems.forEach((child) => {
                const childName = (child.querySelector("span")?.textContent ?? "").toLowerCase();
                const childMatches = q === "" || childName.includes(q);
                child.hidden = !(parentMatches || childMatches);
                if (childMatches) hasMatchingChild = true;
            });

            (group as HTMLElement).hidden = !parentMatches && !hasMatchingChild;

            // Auto-expand when search finds matching children
            if (q !== "" && hasMatchingChild) {
                const childrenEl = group.querySelector(`[data-role="${Roles.FILTER_SIDEBAR_CAT_CHILDREN}"]`) as HTMLElement | null;
                const toggleBtn = group.querySelector(`[data-role="${Roles.FILTER_SIDEBAR_CAT_TOGGLE}"]`);
                if (childrenEl) childrenEl.classList.add("is-open");
                if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "true");
            }
        });
    }

    public filterBrands(query: string, pairs: BrandItemPair[]): void {
        const q = query.toLowerCase().trim();
        pairs.forEach(({ label, checkbox }) => {
            const name = (label.dataset.brandName ?? "").toLowerCase();
            const matches = q === "" || name.includes(q);
            label.hidden = !matches;
            if (checkbox) checkbox.checked = matches && q !== "";
        });
    }

    public navigate(url: string): void {
        globalThis.location.href = url;
    }
}
